import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { CalendarDays, Users, BarChart3, LogOut, Settings, MessageSquare, FileSignature, FileText, MoreHorizontal, ChevronDown, Contact2, Wrench, Search, Activity, FileStack, Bug, Plus, StickyNote, Clock, CheckSquare, Calendar, UserPlus, Briefcase, DollarSign, ListPlus } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import FirmSwitcher from './FirmSwitcher';
import GlobalTimerWidget from './time-tracking/GlobalTimerWidget';
import NotificationBell from './notifications/NotificationBell';
import { useNavbarStatusContext } from '../contexts/NavbarStatusContext';
import BulkTimeEntryModal from './time-tracking/BulkTimeEntryModal';
import SimpleCaseModal from './SimpleCaseModal';
import CalendarEventForm from './calendar/CalendarEventForm';
import firmService from '../services/firmService';
import ClientIntakeModal from './intake/ClientIntakeModal';
import CreateInvoiceModal from './invoices/CreateInvoiceModal';
import QuickAddTaskModal from './quick-actions/QuickAddTaskModal';
import QuickAddNoteModal from './quick-actions/QuickAddNoteModal';

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [showFirmSwitcher, setShowFirmSwitcher] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [showBulkTimeEntry, setShowBulkTimeEntry] = useState(false);
  const [quickActionsOpen, setQuickActionsOpen] = useState(false);
  const [showNewCaseModal, setShowNewCaseModal] = useState(false);
  const [showNewEventModal, setShowNewEventModal] = useState(false);
  const [showClientIntakeModal, setShowClientIntakeModal] = useState(false);
  const [showCreateInvoiceModal, setShowCreateInvoiceModal] = useState(false);
  const [showNewTaskModal, setShowNewTaskModal] = useState(false);
  const [showNewNoteModal, setShowNewNoteModal] = useState(false);
  const userMenuRef = useRef(null);
  const moreMenuRef = useRef(null);
  const quickActionsRef = useRef(null);
  const { timers: navbarTimers, notificationUnreadCount, refetchNavbarStatus } = useNavbarStatusContext();

  useEffect(() => {
    checkFirmSwitcherVisibility();
  }, []);

  // Close dropdowns on route change
  useEffect(() => {
    setUserMenuOpen(false);
    setMoreMenuOpen(false);
    setQuickActionsOpen(false);
  }, [location.pathname]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
      if (moreMenuRef.current && !moreMenuRef.current.contains(event.target)) {
        setMoreMenuOpen(false);
      }
      if (quickActionsRef.current && !quickActionsRef.current.contains(event.target)) {
        setQuickActionsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const checkFirmSwitcherVisibility = async () => {
    try {
      const [adminResult, userFirms] = await Promise.all([
        firmService.checkSuperAdmin(),
        firmService.getUserFirms()
      ]);
      setIsSuperAdmin(adminResult.is_super_admin);
      setShowFirmSwitcher(adminResult.is_super_admin || userFirms.length > 1);
    } catch (err) {
      console.error('Error checking firm switcher visibility:', err);
      setIsSuperAdmin(false);
      setShowFirmSwitcher(false);
    }
  };

  // Core nav items - always visible in the bar
  const coreNavItems = [
    { path: '/', name: 'Dashboard', icon: BarChart3 },
    { path: '/calendar', name: 'Calendar', icon: CalendarDays },
    { path: '/cases', name: 'Cases', icon: Briefcase },
  ];

  // Mid-tier nav items - visible at 1400px+, collapse into "More" on narrower screens
  const midNavItems = [
    { path: '/communications', name: 'Comms', icon: MessageSquare },
    { path: '/billing', name: 'Billing', icon: DollarSign },
  ];

  // Secondary nav items - visible on wide screens (1700px+), collapse into "More" on narrower screens
  const secondaryNavItems = [
    { path: '/clients', name: 'Clients', icon: Users },
    { path: '/tasks', name: 'Tasks', icon: CheckSquare },
    { path: '/documents', name: 'Documents', icon: FileStack },
    { path: '/persons', name: 'Persons', icon: Contact2 },
    { path: '/reports', name: 'Reports', icon: FileText },
    { path: '/signing', name: 'Signing', icon: FileSignature },
    { path: '/tools', name: 'Tools', icon: Wrench },
  ];

  const handleLogout = async () => {
    setUserMenuOpen(false);
    await logout();
  };

  const isItemActive = (item) => {
    return location.pathname === item.path ||
      (item.path !== '/' && location.pathname.startsWith(item.path));
  };

  const renderNavLink = (item) => {
    const Icon = item.icon;
    const isActive = isItemActive(item);

    return (
      <Link
        key={item.path}
        to={item.path}
        className={`flex items-center gap-1.5 px-3 py-2 rounded-lg transition-all duration-200 relative group ${
          isActive
            ? 'bg-yellow-500/30 text-white shadow-lg backdrop-blur-sm'
            : 'text-gray-200 hover:bg-white/10 hover:text-white hover:shadow-md'
        }`}
      >
        <Icon className={`h-5 w-5 transition-transform duration-200 ${!isActive && 'group-hover:scale-110'}`} />
        <span className="hidden xl:block font-medium">{item.name}</span>
      </Link>
    );
  };

  const isOverflowActive = [...midNavItems, ...secondaryNavItems].some(item => isItemActive(item));

  return (<>
    <nav
      className="text-white shadow-xl sticky top-0 z-40"
      style={{ backgroundColor: '#3A3A3A' }}
    >
      <div className="mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center space-x-2 flex-shrink-0">
            <Link to="/" className="relative group">
              <div className="absolute inset-0 bg-white/20 rounded-lg blur opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <img
                src="/justpractice_yellow.png"
                alt="JustPractice"
                className="h-10 min-w-[120px] object-contain relative z-10 transition-transform duration-300 group-hover:scale-105 cursor-pointer"
              />
            </Link>
          </div>

          <div className="flex items-center space-x-1">
            {/* Core nav items - always visible */}
            {coreNavItems.map(renderNavLink)}

            {/* Mid-tier nav items - visible at 1400px+ */}
            <div className="hidden min-[1400px]:contents">
              {midNavItems.map(renderNavLink)}
            </div>

            {/* Secondary nav items - visible on 1700px+ screens */}
            <div className="hidden min-[1700px]:contents">
              {secondaryNavItems.map(renderNavLink)}
            </div>

            {/* "More" dropdown - hidden at 1700px+ when all items are inline */}
            <div className="relative min-[1700px]:hidden" ref={moreMenuRef}>
              <button
                onClick={() => setMoreMenuOpen(!moreMenuOpen)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg transition-all duration-200 group ${
                  isOverflowActive
                    ? 'bg-yellow-500/30 text-white shadow-lg backdrop-blur-sm'
                    : 'text-gray-200 hover:bg-white/10 hover:text-white hover:shadow-md'
                }`}
                aria-label="More navigation items"
                aria-expanded={moreMenuOpen}
                aria-haspopup="true"
              >
                <MoreHorizontal className="h-5 w-5 transition-transform duration-200 group-hover:scale-110" />
                <span className="hidden xl:block font-medium">More</span>
              </button>
              {moreMenuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50 py-1">
                  {/* Mid-tier items shown in dropdown below 1400px */}
                  <div className="min-[1400px]:hidden">
                    {midNavItems.map((item) => {
                      const Icon = item.icon;
                      const isActive = isItemActive(item);
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          onClick={() => setMoreMenuOpen(false)}
                          className={`flex items-center space-x-3 px-4 py-2.5 text-sm transition-colors ${
                            isActive
                              ? 'bg-yellow-50 text-yellow-700 font-medium'
                              : 'text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          <Icon className="h-4 w-4" />
                          <span>{item.name}</span>
                        </Link>
                      );
                    })}
                  </div>
                  {/* Secondary items shown in dropdown below 1700px */}
                  <div className="min-[1700px]:hidden">
                    {secondaryNavItems.map((item) => {
                      const Icon = item.icon;
                      const isActive = isItemActive(item);
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          onClick={() => setMoreMenuOpen(false)}
                          className={`flex items-center space-x-3 px-4 py-2.5 text-sm transition-colors ${
                            isActive
                              ? 'bg-yellow-50 text-yellow-700 font-medium'
                              : 'text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          <Icon className="h-4 w-4" />
                          <span>{item.name}</span>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Right section: Timer + Search + Firm Switcher + User Menu */}
            <div className="flex items-center space-x-3 ml-3 pl-3 border-l border-gray-500/30">
              {/* Quick Actions + Button */}
              <div className="relative" ref={quickActionsRef}>
                <button
                  onClick={() => setQuickActionsOpen(!quickActionsOpen)}
                  className="flex items-center justify-center w-8 h-8 rounded-lg bg-yellow-500 hover:bg-yellow-400 text-gray-900 transition-colors shadow-sm"
                  aria-label="Quick actions"
                  aria-expanded={quickActionsOpen}
                  aria-haspopup="true"
                  title="Quick actions"
                >
                  <Plus className="h-5 w-5" />
                </button>
                {quickActionsOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50 py-1">
                    <button
                      onClick={() => { setQuickActionsOpen(false); setShowNewNoteModal(true); }}
                      className="w-full flex items-center space-x-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <StickyNote className="h-4 w-4" />
                      <span>Add Note</span>
                    </button>
                    <button
                      onClick={() => { setQuickActionsOpen(false); setShowBulkTimeEntry(true); }}
                      className="w-full flex items-center space-x-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <Clock className="h-4 w-4" />
                      <span>Log Time</span>
                    </button>
                    <button
                      onClick={() => { setQuickActionsOpen(false); setShowNewTaskModal(true); }}
                      className="w-full flex items-center space-x-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <CheckSquare className="h-4 w-4" />
                      <span>New Task</span>
                    </button>
                    <button
                      onClick={() => { setQuickActionsOpen(false); setShowNewEventModal(true); }}
                      className="w-full flex items-center space-x-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <Calendar className="h-4 w-4" />
                      <span>New Event</span>
                    </button>
                    <button
                      onClick={() => { setQuickActionsOpen(false); setShowClientIntakeModal(true); }}
                      className="w-full flex items-center space-x-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <UserPlus className="h-4 w-4" />
                      <span>New Client</span>
                    </button>
                    <button
                      onClick={() => { setQuickActionsOpen(false); setShowNewCaseModal(true); }}
                      className="w-full flex items-center space-x-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <Briefcase className="h-4 w-4" />
                      <span>New Case</span>
                    </button>
                    <button
                      onClick={() => { setQuickActionsOpen(false); setShowCreateInvoiceModal(true); }}
                      className="w-full flex items-center space-x-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <DollarSign className="h-4 w-4" />
                      <span>Create Invoice</span>
                    </button>
                  </div>
                )}
              </div>
              {/* Global Timer */}
              <GlobalTimerWidget externalTimers={navbarTimers} refetchNavbarStatus={refetchNavbarStatus} />
              {/* Bulk Time Entry */}
              <button
                onClick={() => setShowBulkTimeEntry(true)}
                className="text-gray-500 hover:text-gray-700 px-2 py-1 rounded hover:bg-gray-100"
                title="Bulk time entry"
              >
                <ListPlus className="h-4 w-4" />
              </button>
              {/* Search button */}
              <button
                onClick={() => window.dispatchEvent(new Event('open-command-palette'))}
                className="flex items-center gap-2 px-3 py-1.5 text-gray-300 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
                aria-label="Search"
              >
                <Search className="h-4 w-4" />
                <span className="hidden xl:inline text-sm">Search</span>
                <kbd className="hidden xl:inline-flex items-center px-1.5 py-0.5 text-xs text-gray-400 bg-white/10 border border-gray-500/30 rounded">
                  Ctrl+K
                </kbd>
              </button>
              {/* Notifications */}
              <NotificationBell externalUnreadCount={notificationUnreadCount} />
              {showFirmSwitcher && (
                <div className="bg-white/10 rounded-lg backdrop-blur-sm">
                  <FirmSwitcher currentUser={user} isSuperAdmin={isSuperAdmin} />
                </div>
              )}

              {/* User dropdown menu */}
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center space-x-2 px-3 py-1.5 bg-white/10 rounded-lg hover:bg-white/20 transition-colors cursor-pointer"
                  aria-label="User menu"
                  aria-expanded={userMenuOpen}
                  aria-haspopup="true"
                >
                  {user?.avatar_url ? (
                    <img
                      src={user.avatar_url}
                      alt={`${user.first_name} ${user.last_name}`}
                      className="w-8 h-8 rounded-full object-cover shadow-md"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold shadow-md text-gray-900" style={{ backgroundColor: '#D4A84B' }}>
                      {user?.first_name?.[0]}{user?.last_name?.[0]}
                    </div>
                  )}
                  <span className="hidden xl:block text-sm text-white font-medium">
                    {user?.first_name} {user?.last_name}
                  </span>
                  <ChevronDown className={`hidden xl:block h-4 w-4 text-gray-400 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
                </button>
                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50 py-1">
                    <div className="px-4 py-2 border-b border-gray-100">
                      <p className="text-sm font-medium text-gray-900">{user?.first_name} {user?.last_name}</p>
                      <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                    </div>
                    <Link
                      to="/settings"
                      onClick={() => setUserMenuOpen(false)}
                      className={`flex items-center space-x-3 px-4 py-2.5 text-sm transition-colors ${
                        location.pathname.startsWith('/settings')
                          ? 'bg-yellow-50 text-yellow-700 font-medium'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <Settings className="h-4 w-4" />
                      <span>Settings</span>
                    </Link>
                    {isSuperAdmin && (
                      <Link
                        to="/admin/observability"
                        onClick={() => setUserMenuOpen(false)}
                        className={`flex items-center space-x-3 px-4 py-2.5 text-sm transition-colors ${
                          location.pathname.startsWith('/admin/observability')
                            ? 'bg-yellow-50 text-yellow-700 font-medium'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <Activity className="h-4 w-4" />
                        <span>Observability</span>
                      </Link>
                    )}
                    {isSuperAdmin && (
                      <Link
                        to="/admin/dev-issues"
                        onClick={() => setUserMenuOpen(false)}
                        className={`flex items-center space-x-3 px-4 py-2.5 text-sm transition-colors ${
                          location.pathname.startsWith('/admin/dev-issues')
                            ? 'bg-yellow-50 text-yellow-700 font-medium'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <Bug className="h-4 w-4" />
                        <span>Dev Issues</span>
                      </Link>
                    )}
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center space-x-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <LogOut className="h-4 w-4" />
                      <span>Logout</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </nav>
    {showBulkTimeEntry && (
      <BulkTimeEntryModal
        isOpen={showBulkTimeEntry}
        onClose={() => setShowBulkTimeEntry(false)}
        onSuccess={() => setShowBulkTimeEntry(false)}
      />
    )}
    {showNewCaseModal && (
      <SimpleCaseModal
        isOpen={showNewCaseModal}
        onClose={() => setShowNewCaseModal(false)}
        onSuccess={() => { setShowNewCaseModal(false); navigate('/cases'); }}
      />
    )}
    {showNewEventModal && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <CalendarEventForm
            onClose={() => setShowNewEventModal(false)}
            onEventCreated={() => { setShowNewEventModal(false); navigate('/calendar'); }}
          />
        </div>
      </div>
    )}
    {showClientIntakeModal && (
      <ClientIntakeModal
        isOpen={showClientIntakeModal}
        onClose={() => setShowClientIntakeModal(false)}
        onSuccess={(newClient) => { setShowClientIntakeModal(false); navigate(`/clients/${newClient.id}`); }}
      />
    )}
    {showCreateInvoiceModal && (
      <CreateInvoiceModal
        onClose={() => setShowCreateInvoiceModal(false)}
        onSuccess={() => { setShowCreateInvoiceModal(false); navigate('/invoices'); }}
      />
    )}
    {showNewTaskModal && (
      <QuickAddTaskModal
        isOpen={showNewTaskModal}
        onClose={() => setShowNewTaskModal(false)}
        onSuccess={() => setShowNewTaskModal(false)}
      />
    )}
    {showNewNoteModal && (
      <QuickAddNoteModal
        isOpen={showNewNoteModal}
        onClose={() => setShowNewNoteModal(false)}
        onSuccess={() => setShowNewNoteModal(false)}
      />
    )}
    </>
  );
};

export default Navbar;
